/**
 * Редактор курса
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCourse, createCourse, updateCourse, Course, Section, Module } from '../../lib/admin-api';
import ModuleEditor from './ModuleEditor';
import AdminShell from './AdminShell';
import CourseOutline from './CourseOutline';

const CATEGORIES = [
  { id: 'technology', name: '💻 Технологии' },
  { id: 'cinema', name: '🎬 Кино' },
  { id: 'history', name: '📜 История' },
  { id: 'science', name: '🔬 Наука' },
  { id: 'languages', name: '🌍 Языки' },
  { id: 'entertainment', name: '🎮 Развлечения' },
  { id: 'tourism', name: '🏕️ Туризм' },
];

const DIFFICULTIES = [
  { id: 'beginner', name: '🟢 Начальный' },
  { id: 'intermediate', name: '🟡 Средний' },
  { id: 'advanced', name: '🔴 Продвинутый' },
];

const ICONS = ['📚', '🐍', '💻', '🎬', '🎮', '🔬', '📜', '🌍', '🎯', '🧠', '💡', '🚀', '📱', '🚣', '🏕️'];

const ACCESS_TYPES = [
  { id: 'free', name: '🟢 Бесплатный', desc: 'Весь курс открыт' },
  { id: 'freemium', name: '🟡 Частично платный', desc: 'Часть секций бесплатно, остальные за Stars' },
  { id: 'premium', name: '🔴 Платный', desc: 'Только описание бесплатно, весь контент за Stars' },
  { id: 'private', name: '🔒 Личный', desc: 'Виден и открыт только перечисленным людям (через Telegram)' },
];
// Владелец по умолчанию в списке личного курса — иначе курс не откроет никто
const OWNER_TG_USER = 'tg_1106016989';

export default function AdminCourseEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [course, setCourse] = useState<Partial<Course & { access?: any }>>({
    name: '',
    description: '',
    icon: '📚',
    category: 'technology',
    difficulty: 'beginner',
    sections: [],
    access: { type: 'free' },
  });
  const [editingModule, setEditingModule] = useState<{ sectionIndex: number; moduleIndex: number; module: Module } | null>(null);
  const [moduleEditorSection, setModuleEditorSection] = useState<'lesson' | 'quiz'>('lesson');
  const [editorKey, setEditorKey] = useState(0);

  useEffect(() => {
    if (!isNew && id) {
      getCourse(id)
        .then(data => {
          if (!(data as any).access) (data as any).access = { type: 'free' };
          setCourse(data);
        })
        .catch(() => window.dispatchEvent(new CustomEvent('app-toast', { detail: { message: 'Ошибка загрузки', type: 'error' } })))
        .finally(() => setLoading(false));
    }
  }, [id, isNew]);

  const access = course.access || { type: 'free' };
  const sectionsCount = (course.sections || []).length;

  const setAccess = (updates: any) => {
    setCourse({ ...course, access: { ...access, ...updates } });
  };

  const handleSave = async () => {
    if (!course.name?.trim()) {
      window.dispatchEvent(new CustomEvent('app-toast', { detail: { message: 'Введите название курса', type: 'error' } }));
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const created = await createCourse(course);
        navigate(`/admin/courses/${created.id}`, { replace: true });
      } else {
        await updateCourse(id!, course);
      }
      window.dispatchEvent(new CustomEvent('app-toast', { detail: { message: 'Сохранено!', type: 'success' } }));
    } catch (err) {
      window.dispatchEvent(new CustomEvent('app-toast', { detail: { message: 'Ошибка сохранения', type: 'error' } }));
    } finally {
      setSaving(false);
    }
  };

  const addSection = () => {
    const sections = [...(course.sections || [])];
    sections.push({
      id: `section_${Date.now()}`,
      name: 'Новая секция',
      modules: [],
    });
    setCourse({ ...course, sections });
  };

  const updateSection = (index: number, updates: Partial<Section>) => {
    const sections = [...(course.sections || [])];
    sections[index] = { ...sections[index], ...updates };
    setCourse({ ...course, sections });
  };

  const deleteSection = (index: number) => {
    if (!window.confirm('Удалить секцию?')) return; // admin only
    const sections = [...(course.sections || [])];
    sections.splice(index, 1);
    setCourse({ ...course, sections });
  };

  const addModule = (sectionIndex: number) => {
    const sections = [...(course.sections || [])];
    sections[sectionIndex].modules.push({
      id: `module_${Date.now()}`,
      name: 'Новый модуль',
      description: '',
      quizIds: [],
      requiredScore: 70,
    });
    setCourse({ ...course, sections });
  };

  const deleteModule = (sectionIndex: number, moduleIndex: number) => {
    if (!window.confirm('Удалить модуль?')) return; // admin only
    const sections = [...(course.sections || [])];
    sections[sectionIndex].modules.splice(moduleIndex, 1);
    setCourse({ ...course, sections });
  };

  const handleModuleSave = async (updatedModule: Module) => {
    if (!editingModule) return;
    const sections = [...(course.sections || [])];
    sections[editingModule.sectionIndex].modules[editingModule.moduleIndex] = updatedModule;
    const updatedCourse = { ...course, sections };
    setCourse(updatedCourse);
    // Раньше здесь стояло setEditingModule(null): «Сохранить» сохраняло И закрывало
    // редактор. Владелец 10.09.2026: «я просто хочу сохранить, зачем меня выкидывает».
    // Теперь окно остаётся открытым, а состояние модуля обновляется на месте.
    setEditingModule({ ...editingModule, module: updatedModule });
    try {
      if (id) await updateCourse(id, updatedCourse);
    } catch (err) {
      console.error("Failed to save course after module edit:", err);
    }
  };

  if (loading) {
    return (
      <AdminShell title="Редактор курса" onBack={() => navigate('/admin/courses')}>
        <div className="flex min-h-[50vh] items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#0F766E] border-t-transparent rounded-full animate-spin" />
      </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell
      title={isNew ? 'Новый курс' : 'Редактирование курса'}
      onBack={() => navigate('/admin/courses')}
      actions={<button onClick={handleSave} disabled={saving} className="rounded-lg bg-[#0F766E] px-3 py-2 text-sm text-white hover:bg-[#0B5F59] disabled:opacity-50">
        {saving ? 'Сохранение...' : 'Сохранить'}
      </button>}
    >
      <div className="grid items-start gap-6 lg:grid-cols-[360px_minmax(0,1fr)]">
        <CourseOutline sections={course.sections || []} />
        <main className="min-w-0 space-y-6 pb-12">
        {/* Основное */}
        <section className="bg-white border border-[#E1E5E3] rounded-lg p-4">
          <h2 className="font-semibold mb-4">Основное</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-[#626B73] mb-1">Название</label>
              <input
                type="text"
                value={course.name || ''}
                onChange={e => setCourse({ ...course, name: e.target.value })}
                className="w-full bg-white border border-[#E1E5E3] rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
                placeholder="Python для начинающих"
              />
            </div>

            <div>
              <label className="block text-sm text-[#626B73] mb-1">Описание</label>
              <textarea
                value={course.description || ''}
                onChange={e => setCourse({ ...course, description: e.target.value })}
                className="w-full bg-white border border-[#E1E5E3] rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0F766E] h-20"
                placeholder="Изучи основы Python с нуля..."
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className="block text-sm text-[#626B73] mb-1">Иконка</label>
                <div className="flex flex-wrap gap-2">
                  {ICONS.map(icon => (
                    <button
                      key={icon}
                      onClick={() => setCourse({ ...course, icon })}
                      className={`text-2xl p-1 rounded ${
                        course.icon === icon ? 'bg-[#E5F2EF] ring-1 ring-[#0F766E]' : 'hover:bg-[#E5F2EF]'
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#626B73] mb-1">Категория</label>
                <select
                  value={course.category || 'technology'}
                  onChange={e => setCourse({ ...course, category: e.target.value })}
                  className="w-full bg-white border border-[#E1E5E3] rounded px-3 py-2"
                >
                  {CATEGORIES.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-[#626B73] mb-1">Уровень</label>
                <select
                  value={course.difficulty || 'beginner'}
                  onChange={e => setCourse({ ...course, difficulty: e.target.value })}
                  className="w-full bg-white border border-[#E1E5E3] rounded px-3 py-2"
                >
                  {DIFFICULTIES.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </section>

        {/* Доступ и цена */}
        <section className="bg-white border border-[#E1E5E3] rounded-lg p-4">
          <h2 className="font-semibold mb-4">💰 Доступ и цена</h2>

          <div className="space-y-4">
            {/* Вид доступа */}
            <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
              {ACCESS_TYPES.map(at => (
                <button
                  key={at.id}
                  onClick={() => {
                    const updates: any = { type: at.id };
                    if (at.id === 'free') {
                      delete updates.price;
                      delete updates.previewSections;
                      setAccess({ type: 'free', price: undefined, previewSections: undefined });
                      return;
                    }
                    if (at.id === 'premium') {
                      updates.previewSections = 0;
                      updates.price = access.price || 50;
                    }
                    if (at.id === 'freemium') {
                      updates.previewSections = access.previewSections || Math.min(2, sectionsCount);
                      updates.price = access.price || 50;
                    }
                    if (at.id === 'private') {
                      setAccess({ type: 'private', price: undefined, previewSections: undefined,
                                  allowedUsers: access.allowedUsers?.length ? access.allowedUsers : [OWNER_TG_USER] });
                      return;
                    }
                    setAccess(updates);
                  }}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    access.type === at.id
                      ? 'border-[#0F766E] bg-[#E5F2EF]'
                      : 'border-[#E1E5E3] hover:border-[#E1E5E3]'
                  }`}
                >
                  <div className="text-sm font-medium">{at.name}</div>
                  <div className="text-xs text-[#626B73] mt-1">{at.desc}</div>
                </button>
              ))}
            </div>

            {/* Личный: кому виден */}
            {access.type === 'private' && (
              <div className="bg-[#F7F7F4] rounded-lg p-4 space-y-2">
                <label className="block text-sm text-[#626B73]">Кому виден (id Telegram, через запятую)</label>
                <input
                  type="text"
                  value={(access.allowedUsers || []).map((u: string) => u.replace(/^tg_/, '')).join(', ')}
                  onChange={e => setAccess({ allowedUsers: e.target.value.split(',').map(x => x.trim()).filter(Boolean).map(x => (x.startsWith('tg_') ? x : 'tg_' + x)) })}
                  className="w-full bg-white border border-[#E1E5E3] rounded px-3 py-2 text-sm"
                  placeholder="1106016989"
                />
                <p className="text-xs text-[#626B73]">Для всех остальных курса не существует: его нет в каталоге, по ссылке пусто. На сайте без Telegram он не виден никому. Открыть курс людям — сменить вид доступа.</p>
              </div>
            )}

            {/* Цена */}
            {access.type !== 'free' && access.type !== 'private' && (
              <div className="bg-[#F7F7F4] rounded-lg p-4 space-y-4">
                <div>
                  <label className="block text-sm text-[#626B73] mb-1">Получатель оплаты на сайте (магазин ЮKassa)</label>
                  <select
                    value={access.payeeId || ''}
                    onChange={e => setAccess({ payeeId: e.target.value || undefined })}
                    className="w-full bg-white border border-[#E1E5E3] rounded px-3 py-2 text-sm"
                  >
                    <option value="">по умолчанию (beshek)</option>
                    <option value="beshek">beshek — игры на сайте</option>
                    <option value="kurs">kurs — курсы (после одобрения магазина)</option>
                    <option value="test">test — тестовый магазин</option>
                  </select>
                  <p className="text-xs text-[#626B73] mt-1">Сменить самозанятого у курса — поменять это поле. Список магазинов: data/payees.json.</p>
                </div>
                <div>
                  <label className="block text-sm text-[#626B73] mb-1">Цена (Stars ⭐)</label>
                  <input
                    type="number"
                    min="1"
                    max="10000"
                    value={access.price || 50}
                    onChange={e => setAccess({ price: parseInt(e.target.value) || 50 })}
                    className="w-32 bg-white border border-[#E1E5E3] rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
                  />
                </div>

                {/* Бесплатные секции */}
                {access.type === 'freemium' && (
                  <div>
                    <label className="block text-sm text-[#626B73] mb-1">
                      Бесплатных секций: <span className="text-[#252A2D] font-medium">{access.previewSections ?? 2}</span> из {sectionsCount}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max={sectionsCount}
                      value={access.previewSections ?? 2}
                      onChange={e => setAccess({ previewSections: parseInt(e.target.value) })}
                      className="w-full accent-[#0F766E]"
                    />
                    <div className="flex justify-between text-xs text-[#626B73] mt-1">
                      <span>0 (только описание)</span>
                      <span>{sectionsCount} (всё бесплатно)</span>
                    </div>
                    {sectionsCount > 0 && (
                      <div className="mt-3 space-y-1">
                        {(course.sections || []).map((s, i) => (
                          <div key={s.id} className={`text-xs px-2 py-1 rounded ${
                            i < (access.previewSections ?? 2)
                              ? 'bg-[#E5F2EF] text-[#0F766E]'
                              : 'bg-red-50 text-[#DC2626]'
                          }`}>
                            {i < (access.previewSections ?? 2) ? '🔓' : '🔒'} {s.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Виджет */}
                <div className="text-xs text-[#626B73] border-t border-[#E1E5E3] pt-3">
                  <span className="text-[#626B73]">В embed-виджете:</span>{' '}
                  {access.type === 'premium'
                    ? 'Только описание → CTA «Открыть в Telegram»'
                    : `Первые ${access.previewSections ?? 2} секции открыты → остальные CTA «Открыть в Telegram»`}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Структура */}
        <section className="rounded-lg border border-[#E1E5E3] bg-white p-4">
          <div className="flex flex-wrap gap-2 items-center justify-between mb-4">
            <h2 className="font-semibold">Структура курса</h2>
            <button
              onClick={addSection}
              className="px-3 py-1 bg-[#0F766E] text-white rounded text-sm hover:bg-[#0B5F59]"
            >
              + Добавить секцию
            </button>
          </div>

          <div className="space-y-4">
            {(course.sections || []).map((section, sIndex) => (
              <div key={section.id} className="bg-white border border-[#E1E5E3] rounded-lg p-2 sm:p-3">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="text-lg">📂</span>
                  <input
                    type="text"
                    value={section.name}
                    onChange={e => updateSection(sIndex, { name: e.target.value })}
                    className="min-w-0 flex-1 bg-white border border-[#E1E5E3] rounded px-3 py-1 text-sm"
                    aria-label="Название секции"
                    placeholder="Название секции"
                  />
                  <button
                    onClick={() => addModule(sIndex)}
                    className="px-2 py-1 bg-[#0F766E] text-white rounded text-xs"
                  >
                    + Модуль
                  </button>
                  <button
                    title="Удалить секцию" aria-label="Удалить секцию"
                    onClick={() => deleteSection(sIndex)}
                    className="px-2 py-1 bg-red-50 text-[#DC2626] rounded text-xs"
                  >
                    ✕
                  </button>
                </div>

                <div className="space-y-1">
                  {section.modules.map((module, mIndex) => (
                    <div key={module.id} id={`course-module-${sIndex}-${mIndex}`} className="course-module bg-[#F7F7F4] rounded p-2 grid grid-cols-[auto_minmax(0,1fr)] items-start gap-2 sm:grid-cols-[auto_minmax(0,1fr)_auto]">
                      <span>{module.lesson ? "📝" : "📋"}</span>
                      <div className="min-w-0 flex-1 space-y-2">
                        <input
                          aria-label="Название модуля"
                          value={module.name}
                          onChange={e => updateSection(sIndex, { modules: section.modules.map((item, index) => index === mIndex ? { ...item, name: e.target.value } : item) })}
                          className="w-full rounded px-2 py-1 text-sm font-medium"
                        />
                        {module.description && (
                          <div className="text-xs text-[#626B73]">{module.description}</div>
                        )}
                        <div className="flex flex-wrap items-center gap-2 text-xs text-[#626B73]">
                          <span>Урок: {module.lesson ? "✅" : "❌"}</span>
                          <span>Тест: {module.quizIds?.length ? "✅" : "❌"}</span>
                        </div>
                      </div>
                      <div className="col-start-2 flex gap-1 sm:col-start-auto">
                        <button
                          title="Урок" aria-label="Редактировать урок"
                          onClick={() => { setModuleEditorSection('lesson'); setEditorKey(k => k + 1); setEditingModule({ sectionIndex: sIndex, moduleIndex: mIndex, module }); }}
                          className="px-2 py-1 bg-[#0F766E] text-white rounded text-xs hover:bg-[#0B5F59]"
                        >
                          📖
                        </button>
                        <button
                          title="Тест" aria-label="Редактировать тест"
                          onClick={() => { setModuleEditorSection('quiz'); setEditorKey(k => k + 1); setEditingModule({ sectionIndex: sIndex, moduleIndex: mIndex, module }); }}
                          className="rounded bg-[#E5F2EF] px-2 py-1 text-xs text-[#0F766E] hover:bg-[#E1E5E3]"
                        >📋</button>
                        <button
                          title="Удалить" aria-label="Удалить модуль"
                          onClick={() => deleteModule(sIndex, mIndex)}
                          className="px-2 py-1 bg-red-50 text-[#DC2626] rounded text-xs hover:bg-red-50"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                  {section.modules.length === 0 && (
                    <p className="text-[#626B73] text-sm text-center py-2">Нет модулей</p>
                  )}
                </div>
              </div>
            ))}

            {(course.sections || []).length === 0 && (
              <div className="text-center py-8 text-[#626B73]">
                <p>Добавьте первую секцию</p>
              </div>
            )}
          </div>
        </section>
      </main>
      </div>

      {/* Редактор модуля */}
      {editingModule && (
        <ModuleEditor
          key={editorKey}
          initialSection={moduleEditorSection}
          courseId={course.id || "new_course"}
          module={editingModule.module}
          onSave={handleModuleSave}
          onClose={() => setEditingModule(null)}
        />
      )}
    </AdminShell>
  );
}